import React, { useEffect, useState } from 'react';
import { useNotification } from '../../../../context/NotificationContext';
import { generateAdminSecretCode } from '../../../admin-auth/access-code/generateAdminSecretCode';
import { getAdminGateMode, setAdminGateMode } from '../../../api/systemStatusApi';

const AdminAccessCodeSettingsCard = () => {
  const { showToast } = useNotification();
  const [enabled, setEnabled] = useState(false);
  const [hasAccessCode, setHasAccessCode] = useState(false);
  const [envConfigured, setEnvConfigured] = useState(false);
  const [codeDraft, setCodeDraft] = useState('');
  const [saving, setSaving] = useState(false);
  const [codeVisible, setCodeVisible] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const data = await getAdminGateMode();
        if (cancelled) return;
        setEnabled(Boolean(data.enabled));
        setHasAccessCode(Boolean(data.hasAccessCode));
        setEnvConfigured(Boolean(data.envConfigured));
      } catch (error) {
        console.error('Error loading admin access code settings:', error);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, []);

  const save = async ({ accessCode, nextEnabled } = {}) => {
    setSaving(true);
    try {
      const result = await setAdminGateMode({
        accessCode,
        enabled: nextEnabled,
      });
      setEnabled(Boolean(result.enabled));
      setHasAccessCode(Boolean(result.hasAccessCode));
      setEnvConfigured(Boolean(result.envConfigured));
      if (accessCode) {
        setCodeDraft(accessCode);
        setCodeVisible(true);
      }
      showToast('Admin access code updated.', 'success');
    } catch (err) {
      showToast(err?.message || 'Failed to update admin access code', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleCopyCode = async () => {
    if (!codeDraft.trim()) return;
    try {
      await navigator.clipboard.writeText(codeDraft.trim());
      showToast('Access code copied.', 'success');
    } catch {
      showToast('Could not copy the code.', 'error');
    }
  };

  return (
    <section className="admin-card app-settings-card">
      <div className="app-settings-row">
        <div className="app-settings-row__text">
          <h3 className="app-settings-row__title">Admin login access code</h3>
          <p className="app-settings-row__desc">
            Show the access code page before the admin login form. People with a trusted
            device can skip it for 30 days. Turn this off on local so the secret is not required.
          </p>
        </div>
        <div className="app-settings-row__action">
          <label className="pkg-dialog-check" htmlFor="admin-access-code-mode">
            <input
              id="admin-access-code-mode"
              type="checkbox"
              checked={enabled}
              disabled={saving || (!hasAccessCode && !envConfigured && !codeDraft.trim())}
              onChange={() => save({ nextEnabled: !enabled, accessCode: codeDraft })}
            />
            Enabled
          </label>
        </div>
      </div>

      <div className="app-settings-row app-settings-row--stacked">
        <p className="app-settings-help">
          {hasAccessCode
            ? envConfigured
              ? 'An access code is already saved, and a server environment code is also configured.'
              : 'An access code is already saved. Enter a new one only if you want to replace it.'
            : process.env.NODE_ENV === 'production'
              ? 'Generate a code, copy it, then save it. The access code page stays hidden until one is saved.'
              : 'On local the default code is admin until you save a different one.'}
        </p>
        <div className="platform-credentials-form">
          <div className="pkg-dialog-field">
            <label htmlFor="admin-login-access-code">Access code</label>
            <input
              id="admin-login-access-code"
              className="pkg-dialog-input app-settings-secret-input"
              type={codeVisible ? 'text' : 'password'}
              value={codeDraft}
              onChange={(e) => setCodeDraft(e.target.value)}
              placeholder={hasAccessCode ? '••••••••••••••••••••••••••••••••' : 'Access code'}
              maxLength={64}
              autoComplete="off"
              spellCheck="false"
            />
          </div>
          <div className="platform-credentials-actions">
            <button
              type="button"
              className="pkg-btn pkg-btn-secondary"
              disabled={saving}
              onClick={() => {
                setCodeDraft(generateAdminSecretCode());
                setCodeVisible(true);
              }}
            >
              Generate code
            </button>
            <button
              type="button"
              className="pkg-btn pkg-btn-secondary"
              disabled={saving || !codeDraft.trim()}
              onClick={handleCopyCode}
            >
              Copy
            </button>
            <button
              type="button"
              className="pkg-btn pkg-btn-primary"
              disabled={saving || !codeDraft.trim()}
              onClick={() => save({ accessCode: codeDraft, nextEnabled: enabled })}
            >
              {saving ? 'Saving…' : 'Save code'}
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default AdminAccessCodeSettingsCard;
